import { useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from "@mui/material"
import { toast } from "react-toastify"

import {
  IntegrationCatalogueDocument,
  useConnectIntegrationMutation
} from "src/graphql/quark/quark.type"

type SettingSpec = {
  key: string
  label: string
  helpText?: string | null
  secret: boolean
  required: boolean
}

type CatalogueEntry = {
  connectorCode: string
  displayName: string
  description: string
  settings: SettingSpec[]
}

const ConnectIntegrationModal = ({
  entry,
  onClose,
  onConnected
}: {
  entry: CatalogueEntry | null
  onClose: () => void
  onConnected: (connectionId: string) => void
}) => {
  const [name, setName] = useState("")
  const [environment, setEnvironment] = useState("sandbox")
  const [values, setValues] = useState<Record<string, string>>({})

  const [connect, { loading }] = useConnectIntegrationMutation({
    refetchQueries: [{ query: IntegrationCatalogueDocument }]
  })

  const handleClose = () => {
    setName("")
    setEnvironment("sandbox")
    setValues({})
    onClose()
  }

  const handleConnect = async () => {
    if (!entry) return

    const settings = entry.settings
      .filter((spec) => !spec.secret)
      .map((spec) => ({ key: spec.key, value: values[spec.key] ?? "" }))
    const credentials = entry.settings
      .filter((spec) => spec.secret)
      .map((spec) => ({ key: spec.key, value: values[spec.key] ?? "" }))

    try {
      const result = await connect({
        variables: {
          input: {
            connectorCode: entry.connectorCode,
            displayName: name.trim() || entry.displayName,
            environment,
            settings,
            credentials
          }
        }
      })
      const id = result.data?.connectFusionIntegration.id
      toast.success(`${entry.displayName} connected`)
      handleClose()
      if (id) onConnected(id)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not connect")
    }
  }

  const missingRequired = (entry?.settings ?? []).some(
    (spec) => spec.required && !(values[spec.key] ?? "").trim()
  )

  return (
    <Dialog open={!!entry} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Connect {entry?.displayName}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {entry?.description}
        </Typography>

        <TextField
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          label="Name"
          placeholder={entry?.displayName}
          helperText="How this integration appears in your list."
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <TextField
          select
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          label="Environment"
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
        >
          <MenuItem value="sandbox">Sandbox</MenuItem>
          <MenuItem value="production">Production</MenuItem>
        </TextField>

        {(entry?.settings ?? []).map((spec) => (
          <TextField
            key={spec.key}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            type={spec.secret ? "password" : "text"}
            label={spec.label}
            helperText={spec.helpText ?? undefined}
            required={spec.required}
            value={values[spec.key] ?? ""}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, [spec.key]: e.target.value }))
            }
          />
        ))}

        <Alert severity="info">
          After connecting you will get a webhook URL to paste into the
          provider&apos;s portal.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || missingRequired}
          onClick={() => void handleConnect()}
        >
          Connect
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConnectIntegrationModal
